// ========== 消息类型常量 ==========

export const MessageType = {
  REGISTER: 'register',
  UNREGISTER: 'unregister',
  CALL: 'call',
  INITIATE_CALL: 'initiate_call',
  INCOMING_CALL: 'incoming_call',
  ACCEPT: 'accept',
  REJECT: 'reject',
  HANGUP: 'hangup',
  SFU_JOIN: 'sfu_join',
  SFU_OFFER: 'sfu_offer',
  SFU_ANSWER: 'sfu_answer',
  SFU_ICE: 'sfu_ice',
  SFU_LEAVE: 'sfu_leave',
  CALL_STATE: 'call_state',
  ERROR: 'error',
  ACK: 'ack',
  HEARTBEAT: 'heartbeat',
} as const

export type MessageType = (typeof MessageType)[keyof typeof MessageType]

// ========== 角色与状态 ==========

export type Role = 'device' | 'operator'

export type CallState =
  | 'calling'
  | 'accepted'
  | 'negotiating'
  | 'connected'
  | 'ended'
  | 'cancelled'

// ========== 通用消息结构 ==========

export interface Message<T extends string = MessageType, D = unknown> {
  type: T
  data?: D
}

// ========== 各消息数据结构 ==========

export interface RegisterData {
  role: Role
  id: string
  name: string
  meta?: Record<string, string>
}

export interface CallData {
  call_id: string
  device_id: string
  device_name: string
  group?: string
}

export interface InitiateCallData {
  device_id: string
}

export interface IncomingCallData {
  call_id: string
  operator_id: string
  operator_name: string
  /** 设备信息（设备告警 CALL 广播时由 SDK 填充，服务端 INCOMING_CALL 消息不携带） */
  device_id?: string
  device_name?: string
}

export interface AcceptData {
  call_id: string
  operator_id?: string
  operator_name?: string
  device_id?: string
  device_name?: string
}

export interface RejectData {
  call_id: string
  operator_id: string
  reason?: string
}

export interface HangupData {
  call_id: string
  by_id: string
  reason?: string
}

export interface SDPData {
  call_id: string
  sdp: string
}

export interface ICEData {
  call_id: string
  candidate: string
  sdp_mid?: string
  sdp_mline_index?: number
}

export interface CallStateData {
  call_id: string
  state: CallState
  operator_id?: string  // 接听者账号 ID
  operator_uid?: number  // 接听者连接 UID（唯一标识 WebSocket 连接）
}

export interface ErrorData {
  code: number
  message: string
  /** 结构化错误类型（如 glare），服务端可选下发，优先于 message 文案匹配 */
  error_type?: string
}

export interface AckData {
  original_type: string
  success: boolean
  message?: string
}

// ========== ICE 服务器 ==========

export interface ICEServer {
  urls: string | string[]
  username?: string
  credential?: string
}

// ========== SFU 消息数据结构 ==========

export interface SFUJoinData {
  call_id: string
  device_id?: string
  device_name?: string
}

export interface SFUJoinNotifyData {
  call_id: string
  role: string
  direction: string
  ice_servers?: ICEServer[]
}

export interface SFUOfferData {
  call_id: string
  sdp: string
  direction?: string
}

export interface SFUAnswerData {
  call_id: string
  sdp: string
}

export interface SFUICEData {
  call_id: string
  candidate: string
  sdp_mid: string
  sdp_mline_index: number
}

export interface SFULeaveData {
  call_id: string
}

// ========== 通话会话信息 ==========

export interface ActiveCallInfo {
  call_id: string
  device_id: string
  device_name: string
  operator_id?: string
  direction: string
  state: string
  sdp?: string
  startedAt: string
}
